import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import getTableViewFromTableQuery from 'mini-mail-list-store/lib/actions/tables/getTableViewFromTableQuery';
import listViewStore from 'mini-mail-list-store/lib/store/Store';
import loadInitialMailTable from './loadInitialMailTable';
import onInitialTableLoadComplete from './onInitialTableLoadComplete';
import TableOperations from 'mini-mail-list-table-operations';
import TableQuery, { TableQueryType } from 'mini-mail-list-store/lib/store/schema/TableQuery';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { action } from 'satcheljs/lib/legacy';
import type { OnInitialTableLoadComplete } from 'mini-mail-search-actions/lib/schema/OnInitialTableLoadComplete';
import type { LoadMailOptions } from '../../schema/LoadMailOptions';

/**
 * Called before loading a new table to the list view store to clean up
 * the old table states
 * @param oldTableView the old table view
 */
function cleanTableStateOnNavigateAway(oldTableView: TableView) {
    /**
     * WI #87925: Remove this switch case after UnifiedSearch is 100%. UnifiedSearch uses removeSearchTableView action.
     */
    switch (oldTableView.tableQuery.type) {
        case TableQueryType.Search:
            // immediately remove search table view since it never gets re-used.
            TableOperations.clear(oldTableView, null /* skipRowsNewerThanTime*/);
            listViewStore.tableViews.delete(oldTableView.id);
            break;
        case TableQueryType.Folder:
            oldTableView.selectedItemsIds.clear();
            oldTableView.isInCheckedMode = false;
            oldTableView.isInVirtualSelectAllMode = false;
            oldTableView.focusInfo = { shouldFocusInMailList: false, focusedRowId: null };
            oldTableView.mailHeaderElementToFocus = null;
            break;
    }
}

type LoadInitialSearchTableCallback = (
    tableView: TableView,
    onInitialTableLoadComplete?: OnInitialTableLoadComplete
) => Promise<void>;

/**
 * Load the table from the table query
 * @param tableQuery the table query
 * @param loadInitialSearchTableCallback passed in from search actions
 * @param isLoadingTableViewInBackground Indicates whether table view is loaded on background. Used when loadTableViewFromTableQuery is called
 * from FocusedInbox toggle setting. For example, if you're in a folder "A" other than "inbox", and you toggle the setting,
 * we would want to refresh the inbox data, but not keep the tableView still on folder "A"
 * @return a promise that resolves when the load table from server has completed
 */
export default action('loadTableViewFromTableQuery')(function loadTableViewFromTableQuery(
    tableQuery: TableQuery,
    loadInitialSearchTableCallback?: LoadInitialSearchTableCallback,
    isLoadingTableViewInBackground?: boolean,
    options?: LoadMailOptions
): Promise<void> {
    const oldTableView = getSelectedTableView();
    const newTableView = getTableViewFromTableQuery(tableQuery);

    if (!isLoadingTableViewInBackground) {
        // 1. Set the current selected table in list view store to the new table
        listViewStore.selectedTableViewId = newTableView.id;
    } else {
        // Set the "selectedTableViewId" only when old and new TableView have the same folder id.
        // This is done to update the "selectedTableViewId" with the current "FocusedViewFilter".
        if (
            !oldTableView ||
            oldTableView?.tableQuery?.folderId === newTableView.tableQuery.folderId
        ) {
            listViewStore.selectedTableViewId = newTableView.id;
        }
    }

    // 2. Clear states for the old table view. This should happen after
    // we set the current selected table to a new table as there are components
    // that render based on the current selected table properties and in case of
    // searching withing a search mode the old table will get deleted if we do step 2
    // before setting the selected table to new table
    if (oldTableView) {
        // oldTableView is null when first loading OWA as there is no previously selected table
        cleanTableStateOnNavigateAway(oldTableView);
    }

    // 3. Load Table
    let loadTablePromise: Promise<void>;
    switch (tableQuery.type) {
        case TableQueryType.Folder:
            loadTablePromise = loadInitialMailTable(newTableView, options);
            break;

        case TableQueryType.Search:
            if (!loadInitialSearchTableCallback) {
                throw new Error(
                    'loadInitialSearchTableCallback should not be null when called from search table type'
                );
            }
            loadTablePromise = loadInitialSearchTableCallback(
                newTableView,
                onInitialTableLoadComplete
            );
            break;
    }

    return loadTablePromise;
});
